import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../lib/supabase';
import { Card } from '../components/ui/card';
import Timeline from '../components/Timeline';
import { ArrowLeft, Heart, Activity, Calendar, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function OwnerPetDetail() {
  const { petId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/owner/login');
      return;
    }
    if (user.role !== 'owner') {
      navigate('/clinic/dashboard');
      return;
    }
    fetchPetHistory();
  }, [petId, user, navigate]);

  const fetchPetHistory = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/api/owner/pets/${petId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error('Error al cargar el historial');
      navigate('/owner');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Cargando historial...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { pet, owner, clinic, cases } = data;
  const activeCase = cases.find(c => c.is_active);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/owner" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver a Mis Mascotas
        </Link>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Left Column - Pet Info */}
          <div className="md:col-span-1 space-y-6">
            <Card className="bg-gradient-to-br from-blue-600 to-blue-700 text-white p-6 border-0">
              <div className="flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mx-auto mb-4">
                <Heart className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-center mb-2">{pet.name}</h1>
              <p className="text-blue-100 text-center">
                {pet.species} {pet.breed && `• ${pet.breed}`}
              </p>
              {pet.age && (
                <p className="text-blue-100 text-center mt-1">{pet.age} años</p>
              )}
            </Card>

            <Card className="p-6 bg-white border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-4">Información</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-500">Dueño</p>
                  <p className="text-slate-900">{owner.full_name}</p>
                  <p className="text-sm text-slate-600">{owner.phone}</p>
                </div>
                {clinic && (
                  <div className="border-t border-slate-200 pt-3">
                    <p className="text-sm text-slate-500">Clínica</p>
                    <p className="text-slate-900">{clinic.name}</p>
                  </div>
                )}
              </div>
            </Card>

            {activeCase && (
              <Card className="p-6 bg-green-50 border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">Caso Activo</h3>
                </div>
                <p className="text-sm text-green-700 mb-2">{activeCase.reason}</p>
                <p className="text-xs text-green-600">
                  Estado: {activeCase.status}
                </p>
              </Card>
            )}
          </div>

          {/* Right Column - History */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-xl border border-slate-200 p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Historial Clínico</h2>
              
              {cases.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aún no hay casos registrados</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {cases.map((caseItem, index) => (
                    <div key={caseItem.id} className="border-b border-slate-200 last:border-0 pb-8 last:pb-0">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900">
                            {caseItem.reason}
                          </h3>
                          <p className="text-sm text-slate-500 mt-1">
                            {format(new Date(caseItem.created_at), "d 'de' MMMM 'de' yyyy", { locale: es })}
                          </p>
                        </div>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          caseItem.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                        }`}>
                          {caseItem.status}
                        </span>
                      </div>
                      
                      {caseItem.updates && caseItem.updates.length > 0 && (
                        <div className="mt-6">
                          <h4 className="text-sm font-semibold text-slate-700 mb-4">Actualizaciones:</h4>
                          <Timeline updates={caseItem.updates} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
